import React, { useMemo, useState } from 'react';
import { Menu, ActionIcon, Tooltip } from '@mantine/core';
import {
  IconDotsVertical,
  IconCopy,
  IconMail,
  IconPng,
  IconStar,
  IconCheck,
  IconX,
  IconQrcode,
  IconCreditCard,
  IconShoppingCartCancel,
  IconPrinter,
  IconBell,
  IconRefresh,
  IconArrowsExchange
} from '@tabler/icons-react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { showNotification, updateNotification } from '../../utils/Notification/Notification';
import ConfirmationModal from '../ConfirmationModal/ConfirmationModal';
import {
  useSendEmailReceipt,
  useSendOrderUncollectedCustomerNotification,
  useSetOrderUncollected
} from '../../hooks/orders/mutations';
import { downloadTicketAsPNG } from '../../utils/ticketHelper';
import { useRequestCustomerReview } from '../../hooks/reviews/mutations';
import { downloadOrderCodeAsPNG } from '../OrderActionIcons/DownloadOrderCodeActionIcon';
import { ICompany } from '../../interfaces/Company';
import { IOrder } from '../../interfaces/Order';
import useCanUseMercadoPago from '../../hooks/useCanUseMercadoPago';
import useCanUseOrderPublicReceipt from '../../hooks/useCanUseOrderPublicReceipt';
import OrderStatuses from '../../enum/OrderStatuses';
import useCanMarkOrderAsUncollected from '../../hooks/useCanMarkOrderAsUncollected';
import OperationalReturnDrawer from '../OperationalReturnDrawer';
import { isOrderEligibleForOperationalReturn } from '../../pages/NewOrder/utils/operationalReturns';
import OrderStoreTransferDrawer from '../OrderStoreTransferDrawer';
import OrderNotificationModal from '../OrderNotificationModal';
import useCanEditOrders from '../../hooks/useCanEditOrders';

// Temporal: company IDs que pueden ver "Imprimir media carta" hasta release oficial
const HALF_LETTER_PRINT_ALLOWED_COMPANY_IDS: string[] = [
  '603d6137b34bd70017658b7a',
  '693c85004b74534305e9ee8c'
];

interface MoreActionsProps {
  order: IOrder & { publicToken?: string };
  customColors?: { headerBackgroundColor?: string; headerTextColor?: string };
  showMercadoPagoModal?: (order: IOrder) => void;
  storeId?: string;
  deferMount?: boolean;
  disabled?: boolean;
}

// The board can load many pages. Mount action hooks only for menus the user opens.
const OrdersCardMoreActionsMenu = (props: MoreActionsProps) => {
  const { t } = useTranslation('OrdersCardMoreActionMenu');
  const [activated, setActivated] = useState(!props.deferMount);
  return (
    <Menu
      shadow="md"
      width={300}
      onChange={(opened) => {
        if (opened) setActivated(true);
      }}
    >
      <Tooltip label={t('OrdersCardMoreActionMenu:moreActions')}>
        <Menu.Target>
          <ActionIcon
            variant="transparent"
            aria-label={t('OrdersCardMoreActionMenu:moreActions')}
            color={props.customColors?.headerTextColor}
            disabled={props.disabled}
            data-work-board-no-drag
            onClick={() => setActivated(true)}
          >
            <IconDotsVertical />
          </ActionIcon>
        </Menu.Target>
      </Tooltip>
      {activated && <OrdersCardMoreActionsContent {...props} />}
    </Menu>
  );
};

const OrdersCardMoreActionsContent = ({
  order,
  showMercadoPagoModal,
  storeId
}: MoreActionsProps) => {
  const searchParams = new URLSearchParams(location.search);
  const searchParamStoreId = storeId ?? searchParams.get('storeId') ?? '';
  const { t } = useTranslation([
    'CommonButtons',
    'FieldName',
    'OrdersPage',
    'Notifications',
    'OrdersCardMoreActionMenu'
  ]);
  const { company } = useSelector((state: { user: { company: ICompany } }) => state.user);
  const customer = typeof order.customer === 'object' ? order.customer : undefined;
  const { storeCanUseMP } = useCanUseMercadoPago(searchParamStoreId);
  const sendEmailReceipt = useSendEmailReceipt();
  const requestCustomerReview = useRequestCustomerReview(searchParamStoreId);
  const setOrderUncollected = useSetOrderUncollected();
  const { canUseOrderPublicReceipt } = useCanUseOrderPublicReceipt(searchParamStoreId);
  const sendOrderUncollectedCustomerNotification = useSendOrderUncollectedCustomerNotification();
  const canMarkOrderAsUncollected = useCanMarkOrderAsUncollected();
  const canEditOrders = useCanEditOrders();
  const notificationStoreId = storeId ||
    (typeof order.createdIn === 'string' ? order.createdIn : order.createdIn?._id) ||
    searchParamStoreId;
  const [notificationActivated, setNotificationActivated] = useState(false);
  const [notificationOpened, setNotificationOpened] = useState(false);
  const [returnDrawerActivated, setReturnDrawerActivated] = useState(false);
  const [returnDrawerOpened, setReturnDrawerOpened] = useState(false);
  const [storeTransferDrawerOpened, setStoreTransferDrawerOpened] = useState(false);
  const notificationHandler = (title: string = t('Notifications:loading')) => {
    const notificationId = showNotification({
      type: 'Info',
      title: title,
      autoClose: false
    });
    return {
      notificationId,
      changeNotificationToSuccess: () =>
        updateNotification({
          id: notificationId,
          title: t('Notifications:success'),
          message: t('Notifications:success'),
          color: 'green',
          icon: <IconCheck />,
          autoClose: true
        }),
      changeNotificationToError: (message: string = t('Notifications:error')) =>
        updateNotification({
          id: notificationId,
          title: t('Notifications:error'),
          message,
          color: 'red',
          icon: <IconX />,
          autoClose: true
        }),
      updateNotification: (notification: any) =>
        updateNotification({ id: notificationId, ...notification })
    };
  };

  const onSendOrderUncollectedCustomerNotification = async (order: IOrder) => {
    const notification = notificationHandler(t('Notifications:loading'));
    try {
      if (sendOrderUncollectedCustomerNotification.isPending) return;
      await sendOrderUncollectedCustomerNotification.mutateAsync(order?._id || '');
      notification.changeNotificationToSuccess();
    } catch (e) {
      notification.changeNotificationToError();
    }
  };

  const onRequestCustomerReview = async (customerId: string) => {
    const notification = notificationHandler(t('Notifications:loading'));
    try {
      if (requestCustomerReview.isPending) return;
      await requestCustomerReview.mutateAsync(customerId);
      notification.changeNotificationToSuccess();
    } catch (e) {
      notification.changeNotificationToError();
    }
  };

  const onDownloadPNGTicket = async (order: any) => {
    const notification = notificationHandler(t('Notifications:loading'));
    try {
      await downloadTicketAsPNG(order, order.createdIn, t, 'ticketStructure', company.logoUrl);
      notification.changeNotificationToSuccess();
    } catch (e) {
      console.log('[ERROR] onDownloadPNGTicket:', e);
      notification.changeNotificationToError();
    }
  };

  const onSendEmailReceipt = async (orderId: string) => {
    const notification = notificationHandler(t('Notifications:loading'));
    try {
      if (sendEmailReceipt.isPending) return;
      await sendEmailReceipt.mutateAsync(orderId);
      notification.changeNotificationToSuccess();
    } catch (e) {
      notification.changeNotificationToError();
    }
  };

  const onMarkAsUncollected = async (order: IOrder) => {
    const notification = notificationHandler(t('Notifications:loading'));
    try {
      await setOrderUncollected.mutateAsync({
        orderId: order._id,
        orderSequence: order.sequence ?? '',
        orderStore: typeof order.createdIn === 'string' ? order.createdIn : order?.createdIn?._id,
        reason: 'abandoned' as 'abandoned' | 'sent_to_warehouse' | 'donated' | 'discarded' | 'other'
      });
      notification.changeNotificationToSuccess();
    } catch (e: any) {
      const backendMessage = e?.response?.data?.errors?.[0];
      notification.changeNotificationToError(
        typeof backendMessage === 'string' ? backendMessage : t('Notifications:error')
      );
    }
  };

  const onDownloadOrderCode = async (order: any) => {
    const notification = notificationHandler(t('Notifications:loading'));
    try {
      await downloadOrderCodeAsPNG(order);
      notification.changeNotificationToSuccess();
    } catch (e) {
      notification.changeNotificationToError();
    }
  };

  const actionsByPlanLevel = useMemo(() => {
    const menuSections = {
      receipt: {
        label: t('OrdersCardMoreActionMenu:receipt'),
        items: [] as { label: string; icon: JSX.Element; onClick: () => void; disabled?: boolean }[]
      },
      actions: {
        label: t('OrdersCardMoreActionMenu:actions'),
        items: [] as { label: string; icon: JSX.Element; onClick: () => void; disabled?: boolean }[]
      }
    };

    if (
      canMarkOrderAsUncollected &&
      (order.status === OrderStatuses.READY || order.status === OrderStatuses.DELIVERY)
    ) {
      menuSections.actions.items.push({
        label: t('OrdersCardMoreActionMenu:markAsUncollected'),
        icon: <IconShoppingCartCancel />,
        onClick: () => {
          ConfirmationModal({
            title: t('Notifications:CRUD.markAsUncollectedConfirm'),
            onConfirm: () => onMarkAsUncollected(order),
            onCancel: () => {}
          });
        }
      });
    }

    if (isOrderEligibleForOperationalReturn(order)) {
      menuSections.actions.items.push({
        label: t('OrdersCardMoreActionMenu:generateReturn'),
        icon: <IconRefresh />,
        onClick: () => {
          setReturnDrawerActivated(true);
          setReturnDrawerOpened(true);
        }
      });
    }

    if (canEditOrders) {
      menuSections.actions.items.push({
        label: t('OrdersCardMoreActionMenu:notificationResend.title'),
        icon: <IconBell />,
        disabled: !order._id || !notificationStoreId,
        onClick: () => {
          setNotificationActivated(true);
          setNotificationOpened(true);
        }
      });
    }

    menuSections.actions.items.push({
      label: t('OrdersCardMoreActionMenu:moveToStore'),
      icon: <IconArrowsExchange />,
      onClick: () => setStoreTransferDrawerOpened(true)
    });

    if (order.status === OrderStatuses.READY || order.status === OrderStatuses.UNCOLLECTED) {
      menuSections.actions.items.push({
        label: t('OrdersCardMoreActionMenu:sendOrderUncollectedCustomerNotification'),
        icon: <IconBell />,
        onClick: () => onSendOrderUncollectedCustomerNotification(order)
      });
    }

    menuSections.receipt.items.push({
      disabled: !customer?.email,
      label: t('CommonButtons:sendTicketByMail'),
      icon: <IconMail />,
      onClick: () => {
        ConfirmationModal({
          title: t('OrdersPage:sendTicketByMail.context'),
          onConfirm: () => onSendEmailReceipt(order._id || ''),
          onCancel: () => {}
        });
      }
    });

    menuSections.actions.items.push({
      label: t('OrdersCardMoreActionMenu:requestReview'),
      icon: <IconStar />,
      onClick: () =>
        ConfirmationModal({
          title: t('OrdersPage:requestReviewConfirm.context'),
          onConfirm: () => onRequestCustomerReview(customer?._id || ''),
          onCancel: () => {}
        }),
      disabled: false
    });

    menuSections.actions.items.push({
      label: t('OrdersCardMoreActionMenu:downloadOrderCode'),
      icon: <IconQrcode />,
      onClick: () =>
        ConfirmationModal({
          title: t('CommonButtons:downloadOrderCode.title'),
          onConfirm: () => onDownloadOrderCode(order),
          onCancel: () => {}
        }),
      disabled: false
    });

    menuSections.receipt.items.push({
      label: t('CommonButtons:downloadTicket.title'),
      icon: <IconPng />,
      onClick: () => {
        ConfirmationModal({
          title: t('CommonButtons:downloadTicket.title'),
          onConfirm: () => onDownloadPNGTicket(order),
          onCancel: () => {}
        });
      }
    });

    // Media-letter print (browser) — detrás de flag por company hasta release oficial
    if (company?._id && HALF_LETTER_PRINT_ALLOWED_COMPANY_IDS.includes(company._id)) {
      menuSections.receipt.items.push({
        label: `${t('OrdersCardMoreActionMenu:printHalfLetter')}`,
        icon: <IconPrinter />,
        onClick: () => window.open(`/orders/${order._id}/print/half-letter`, '_blank')
      });
    }

    if (canUseOrderPublicReceipt) {
      menuSections.receipt.items.push({
        label: t('OrdersCardMoreActionMenu:copyOrderPublicReceiptLink'),
        icon: <IconCopy />,
        onClick: () => {
          const notification = notificationHandler(t('Notifications:loading'));

          navigator.clipboard
            .writeText(`${window.location.origin}/receipt/${order.publicToken}`)
            .then(() => {
              notification.updateNotification({
                title: t('Notifications:urlCopied'),
                message: t('Notifications:urlCopied'),
                color: 'green',
                autoClose: true
              });
            })
            .catch(() => {
              notification.changeNotificationToError();
            });
        }
      });
    }

    if (storeCanUseMP && showMercadoPagoModal) {
      menuSections.actions.items.push({
        label: t('OrdersCardMoreActionMenu:mercadoPago.menuTitle'),
        icon: <IconCreditCard />,
        onClick: () => showMercadoPagoModal(order)
      });
    }
    return menuSections;
  }, [
    canMarkOrderAsUncollected,
    canEditOrders,
    notificationStoreId,
    canUseOrderPublicReceipt,
    company,
    customer,
    order,
    showMercadoPagoModal,
    storeCanUseMP,
    t
  ]);

  return (
    <>
      {notificationActivated && (
        <OrderNotificationModal
          opened={notificationOpened}
          onClose={() => setNotificationOpened(false)}
          orderId={order._id || ''}
          storeId={notificationStoreId}
        />
      )}
      {/* The drawer also owns the post-create flow after it closes its source form. */}
      {returnDrawerActivated && (
        <OperationalReturnDrawer
          opened={returnDrawerOpened}
          onClose={() => setReturnDrawerOpened(false)}
          sourceOrder={order}
        />
      )}
      {storeTransferDrawerOpened && (
        <OrderStoreTransferDrawer
          opened={storeTransferDrawerOpened}
          onClose={() => setStoreTransferDrawerOpened(false)}
          order={order}
        />
      )}
      <Menu.Dropdown data-work-board-no-drag>
        {(Object.keys(actionsByPlanLevel) as Array<keyof typeof actionsByPlanLevel>).map(
          (section) => (
            <React.Fragment key={section}>
              <Menu.Label>{actionsByPlanLevel[section].label}</Menu.Label>
              {actionsByPlanLevel[section].items.map(
                (item: {
                  label: string;
                  icon: JSX.Element;
                  onClick: () => void;
                  disabled?: boolean;
                }) => (
                  <Menu.Item
                    key={item.label}
                    leftSection={item.icon}
                    onClick={item.onClick}
                    disabled={item.disabled}
                  >
                    {item.label}
                  </Menu.Item>
                )
              )}
            </React.Fragment>
          )
        )}
      </Menu.Dropdown>
    </>
  );
};

export default OrdersCardMoreActionsMenu;
